import axios, { type GenericAbortSignal } from 'axios'

const RECOVERY_WINDOW_MS = 15_000
const RETRY_INTERVAL_MS = 2_000

export class BadGatewayRecoveryTimeoutError extends Error {}

interface RecoveryWindow {
  deadline: number
  sessionVersion: number
  controller: AbortController
  timer: ReturnType<typeof setTimeout>
  pending: number
  expired: boolean
}

const withCancellation = <T>(task: () => Promise<T>, signal: AbortSignal): Promise<T> =>
  new Promise((resolve, reject) => {
    const onAbort = () => reject(new axios.CanceledError())
    if (signal.aborted) {
      onAbort()
      return
    }

    signal.addEventListener('abort', onAbort, { once: true })
    task()
      .then(resolve, reject)
      .finally(() => signal.removeEventListener('abort', onAbort))
  })

const waitForRetry = (milliseconds: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer)
      reject(new axios.CanceledError())
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, milliseconds)

    signal.addEventListener('abort', onAbort, { once: true })
    if (signal.aborted) onAbort()
  })

export const createBadGatewayRecovery = (options: {
  getSessionVersion: () => number
  onStart: () => void
  onEnd: () => void
  onTimeout: () => void
}) => {
  let activeWindow: RecoveryWindow | null = null

  const closeWindow = (window: RecoveryWindow) => {
    clearTimeout(window.timer)
    window.controller.abort()
    if (activeWindow === window) {
      activeWindow = null
      options.onEnd()
    }
  }

  const expireWindow = (window: RecoveryWindow) => {
    if (window.expired) return

    window.expired = true
    closeWindow(window)
    if (window.sessionVersion === options.getSessionVersion()) options.onTimeout()
  }

  const openWindow = () => {
    const window: RecoveryWindow = {
      deadline: Date.now() + RECOVERY_WINDOW_MS,
      sessionVersion: options.getSessionVersion(),
      controller: new AbortController(),
      timer: setTimeout(() => expireWindow(window), RECOVERY_WINDOW_MS),
      pending: 0,
      expired: false,
    }
    activeWindow = window
    options.onStart()
    return window
  }

  return async <T>(
    retry: (timeout: number, signal: AbortSignal, onResponse: () => void) => Promise<T>,
    signal?: GenericAbortSignal,
  ): Promise<T> => {
    if (signal?.aborted) throw new axios.CanceledError()
    if (activeWindow && activeWindow.sessionVersion !== options.getSessionVersion()) {
      closeWindow(activeWindow)
    }

    const window = activeWindow ?? openWindow()
    window.pending++
    const controller = new AbortController()
    const onAbort = () => controller.abort()
    window.controller.signal.addEventListener('abort', onAbort, { once: true })
    signal?.addEventListener?.('abort', onAbort, { once: true })

    let finished = false
    const finishRequest = () => {
      if (finished) return

      finished = true
      window.controller.signal.removeEventListener('abort', onAbort)
      window.pending--
      if (window.pending === 0) closeWindow(window)
    }

    try {
      while (Date.now() < window.deadline) {
        await waitForRetry(
          Math.min(RETRY_INTERVAL_MS, window.deadline - Date.now()),
          controller.signal,
        )
        const timeout = window.deadline - Date.now()
        if (timeout <= 0) throw new BadGatewayRecoveryTimeoutError()

        try {
          return await withCancellation(
            () => retry(timeout, controller.signal, finishRequest),
            controller.signal,
          )
        } catch (error) {
          if (finished || !axios.isAxiosError(error) || error.response?.status !== 502) throw error
        }
      }
      throw new BadGatewayRecoveryTimeoutError()
    } catch (error) {
      if (
        !finished &&
        (window.expired ||
          error instanceof BadGatewayRecoveryTimeoutError ||
          (!signal?.aborted && Date.now() >= window.deadline))
      ) {
        expireWindow(window)
        throw new BadGatewayRecoveryTimeoutError()
      }
      throw error
    } finally {
      signal?.removeEventListener?.('abort', onAbort)
      finishRequest()
    }
  }
}
